import { i18WithParams as t } from '@shared/locale'
import { MIN_DATE } from 'components-ui/src/shared/constants'
import { dayToJSDate } from 'components-ui/src/shared/dates'
import { format } from 'date-fns'
import { ClientLocationPetProfileService } from 'service-api/src/services/client_location_pet_profile.service'
import type {
  BreedClientDto,
  LocationPetProfileClientDto,
  LocationPetProfileCreateInput,
  LocationSpeciesClientDto,
} from 'service-api/src/types/api'
import slug from 'slug'
import * as yup from 'yup'

export type TFormikValues = {
  id: string
  displayName: string
  breed: BreedClientDto
  weight: string
  birthdate: Date | string
  sex: string
  altered: string
  locationSpeciesId: string
}

export const petSchema = yup.object({
  displayName: yup.string().max(200).required().label(t('global.label.name')),
  breed: yup.object({
    id: yup.string().max(200).required().label(t('global.label.breed')),
  }),
  weight: yup.string().max(200).required().label(t('global.label.weight')),
  locationSpeciesId: yup.string().required(),
  birthdate: yup
    .date()
    .min(MIN_DATE, t('global.errors.invalidDate'))
    .nullable()
    .typeError(t('global.error.invalidValue')),
})

export const setupInitialValues = (
  defaultSpecie: LocationSpeciesClientDto,
  pet?: LocationPetProfileClientDto,
  breed?: BreedClientDto
): TFormikValues => {
  return {
    id: pet?.id || '',
    displayName: pet?.displayName || '',
    breed: pet
      ? ({
          id: pet.breed?.id || pet.breedId || breed?.id,
          displayName: pet.breed?.displayName || breed?.displayName,
        } as BreedClientDto)
      : {},
    weight: String(pet?.weight || ''),
    birthdate: dayToJSDate(pet?.dateOfBirth) || '',
    sex: pet?.sex || '',
    altered: pet?.altered ? 'yes' : 'no',
    locationSpeciesId: pet?.locationSpecies?.id || pet?.locationSpeciesId || defaultSpecie?.id || '',
  }
}

const buildRequestBody = (values: TFormikValues, lupId: string) => {
  return {
    name: slug(values.displayName),
    displayName: values.displayName.trim(),
    breedId: values.breed.id!,
    weight: parseInt(values.weight || '0'),
    dateOfBirth: values.birthdate ? format(new Date(values.birthdate), 'yyyy-MM-dd') : undefined,
    sex: values.sex ? <'MALE' | 'FEMALE'>values.sex : undefined,
    altered: values.altered === 'yes',
    locationSpeciesId: values.locationSpeciesId,
    locationUserProfileId: lupId,
  }
}

export const createPet = async (values: TFormikValues, locationName: string, lupId: string) => {
  const reqBody = buildRequestBody(values, lupId)
  try {
    return await ClientLocationPetProfileService.post(
      locationName,
      lupId,
      reqBody as unknown as LocationPetProfileCreateInput
    )
  } catch (e) {
    return e
  }
}

export const updatePet = async (
  values: TFormikValues,
  locationName: string,
  lupId: string
): Promise<LocationPetProfileClientDto> => {
  const reqBody = buildRequestBody(values, lupId)
  return await ClientLocationPetProfileService.put(locationName, lupId, values.id, reqBody)
}
